import React, { useState } from 'react';
import {
  Box,
  Card,
  CardContent,
  TextField,
  Button,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  Autocomplete,
  Typography,
  Divider,
  IconButton,
  Collapse
} from '@mui/material';
import {
  Search as SearchIcon,
  FilterList as FilterIcon,
  Clear as ClearIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon
} from '@mui/icons-material';
import { useSearchStore } from '../../stores/useSearchStore';

const fileTypes = [
  'pdf',
  'doc',
  'docx',
  'ppt',
  'pptx',
  'xls',
  'xlsx',
  'txt',
  'jpg',
  'jpeg',
  'png',
  'gif',
  'mp4',
  'avi',
  'mp3',
  'wav'
];

const sortOptions = [
  { value: 'relevance', label: 'Độ liên quan' },
  { value: 'date', label: 'Ngày' },
  { value: 'title', label: 'Tiêu đề' }
];

const sortOrderOptions = [
  { value: 'desc', label: 'Mới nhất' },
  { value: 'asc', label: 'Cũ nhất' }
];

export const AdvancedSearchForm: React.FC = () => {
  const { searchRequest, setSearchRequest, search, clearSearch, isLoading } = useSearchStore();
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [tagInput, setTagInput] = useState('');

  const handleInputChange = (field: keyof typeof searchRequest, value: any) => {
    setSearchRequest({ [field]: value });
  };

  const handleTagsChange = (newTags: string[]) => {
    setSearchRequest({ tags: newTags });
  };

  const handleAddTag = () => {
    if (tagInput.trim() && !searchRequest.tags.includes(tagInput.trim())) {
      handleTagsChange([...searchRequest.tags, tagInput.trim()]);
      setTagInput('');
    }
  };

  const handleRemoveTag = (tagToRemove: string) => {
    handleTagsChange(searchRequest.tags.filter(tag => tag !== tagToRemove));
  };

  const handleSearch = async () => {
    await search();
  };

  const handleClear = () => {
    clearSearch();
    setTagInput('');
  };

  const handleKeyPress = (event: React.KeyboardEvent) => {
    if (event.key === 'Enter') {
      handleSearch();
    }
  };

  return (
    <Card>
      <CardContent>
        <Typography variant="h6" gutterBottom>
          Tìm Kiếm Nâng Cao
        </Typography>
        
        {/* Basic Search */}
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '2fr 1fr' }, gap: 2, alignItems: 'center' }}>
          <TextField
            fullWidth
            label="Từ Khóa Tìm Kiếm"
            value={searchRequest.query}
            onChange={(e) => handleInputChange('query', e.target.value)}
            onKeyPress={handleKeyPress}
            placeholder="Nhập từ khóa để tìm kiếm..."
            variant="outlined"
          />
          <Button
            fullWidth
            variant="contained"
            startIcon={<SearchIcon />}
            onClick={handleSearch}
            disabled={isLoading}
            size="large"
          >
            {isLoading ? 'Đang tìm kiếm...' : 'Tìm Kiếm'}
          </Button>
        </Box>

        {/* Advanced Filters Toggle */}
        <Box sx={{ mt: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
          <IconButton
            onClick={() => setShowAdvanced(!showAdvanced)}
            size="small"
          >
            {showAdvanced ? <ExpandLessIcon /> : <ExpandMoreIcon />}
          </IconButton>
          <Typography variant="subtitle2" color="text.secondary">
            Bộ Lọc Nâng Cao
          </Typography>
        </Box>

        {/* Advanced Filters */}
        <Collapse in={showAdvanced}>
          <Box sx={{ mt: 2 }}>
            <Divider sx={{ mb: 2 }} />
            
            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 2 }}>
              {/* Date Range */}
              <TextField
                fullWidth
                label="Ngày Bắt Đầu"
                type="date"
                value={searchRequest.startDate}
                onChange={(e) => handleInputChange('startDate', e.target.value)}
                InputLabelProps={{ shrink: true }}
              />
              <TextField
                fullWidth
                label="Ngày Kết Thúc"
                type="date"
                value={searchRequest.endDate}
                onChange={(e) => handleInputChange('endDate', e.target.value)}
                InputLabelProps={{ shrink: true }}
              />

              {/* File Type and Author */}
              <FormControl fullWidth>
                <InputLabel>Loại File</InputLabel>
                <Select
                  value={searchRequest.fileType}
                  onChange={(e) => handleInputChange('fileType', e.target.value)}
                  label="Loại File"
                >
                  <MenuItem value="">Tất Cả Loại</MenuItem>
                  {fileTypes.map((type) => (
                    <MenuItem key={type} value={type}>
                      {type.toUpperCase()}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <TextField
                fullWidth
                label="Tác Giả"
                value={searchRequest.author}
                onChange={(e) => handleInputChange('author', e.target.value)}
                placeholder="Tìm kiếm theo tên tác giả..."
              />
            </Box>

            {/* Tags */}
            <Box>
              <Typography variant="subtitle2" gutterBottom>
                Thẻ
              </Typography>
                <Box sx={{ display: 'flex', gap: 1, mb: 1 }}>
                  <TextField
                    size="small"
                    placeholder="Thêm thẻ..."
                    value={tagInput}
                    onChange={(e) => setTagInput(e.target.value)}
                    onKeyPress={(e) => {
                      if (e.key === 'Enter') {
                        e.preventDefault();
                        handleAddTag();
                      }
                    }}
                    sx={{ flexGrow: 1 }}
                  />
                  <Button
                    size="small"
                    onClick={handleAddTag}
                    disabled={!tagInput.trim()}
                  >
                    Thêm
                  </Button>
                </Box>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                  {searchRequest.tags.map((tag) => (
                    <Chip
                      key={tag}
                      label={tag}
                      onDelete={() => handleRemoveTag(tag)}
                      size="small"
                    />
                  ))}
                </Box>
              </Box>

            {/* Sort Options */}
            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 2 }}>
              <FormControl fullWidth>
                <InputLabel>Sắp Xếp Theo</InputLabel>
                <Select
                  value={searchRequest.sortBy}
                  onChange={(e) => handleInputChange('sortBy', e.target.value)}
                  label="Sắp Xếp Theo"
                >
                  {sortOptions.map((option) => (
                    <MenuItem key={option.value} value={option.value}>
                      {option.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <FormControl fullWidth>
                <InputLabel>Thứ Tự Sắp Xếp</InputLabel>
                <Select
                  value={searchRequest.sortOrder}
                  onChange={(e) => handleInputChange('sortOrder', e.target.value)}
                  label="Thứ Tự Sắp Xếp"
                >
                  {sortOrderOptions.map((option) => (
                    <MenuItem key={option.value} value={option.value}>
                      {option.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>

            {/* Action Buttons */}
            <Box sx={{ mt: 3, display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
              <Button
                variant="outlined"
                startIcon={<ClearIcon />}
                onClick={handleClear}
              >
                Xóa Tất Cả
              </Button>
              <Button
                variant="contained"
                startIcon={<SearchIcon />}
                onClick={handleSearch}
                disabled={isLoading}
              >
                {isLoading ? 'Đang tìm kiếm...' : 'Tìm Kiếm'}
              </Button>
            </Box>
          </Box>
        </Collapse>
      </CardContent>
    </Card>
  );
};
